import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { QueryObserverResult } from "@tanstack/react-query";
import { contractsOutApi } from "@/lib/api";
import { useOrganizationStore } from "@/stores/organizationStore";
import { DEFAULT_PAGE_SIZE } from "@/lib/constants";
import type {
  ContractOut,
  ContractOutDetails,
  ContractStatus,
  CreateContractOut,
  CreateLicensee,
  Licensee,
  LicensingType,
} from "@/types";

interface ContractsOutFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  ordering?: string;
  status?: ContractStatus[];
  licensingType?: LicensingType[];
  /** Licensee names: variant B has no licensee records to filter by id. */
  licensee?: string[];
  /** Only the contracts out licensing tracks of this contract in. */
  contractIn?: string;
  enabled?: boolean;
}

interface UseContractsOutReturn {
  contractsOut: ContractOut[];
  count: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useContractsOut({
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  search,
  ordering,
  status,
  licensingType,
  licensee,
  contractIn,
  enabled = true,
}: ContractsOutFilters = {}): UseContractsOutReturn {
  const { currentLabel } = useOrganizationStore();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      "contracts-out",
      currentLabel?.id,
      {
        page,
        pageSize,
        search: search?.trim() || null,
        ordering,
        status: status?.join(",") || null,
        licensingType: licensingType?.join(",") || null,
        licensee: licensee?.join(",") || null,
        contractIn,
      },
    ],
    queryFn: () =>
      contractsOutApi.getContractsOut({
        label_id: currentLabel?.id,
        page,
        page_size: pageSize,
        q: search?.trim() || undefined,
        ordering,
        status: status?.length ? status.join(",") : undefined,
        licensing_type: licensingType?.length ? licensingType.join(",") : undefined,
        licensee: licensee?.length ? licensee.join(",") : undefined,
        contract_in: contractIn,
      }),
    enabled: enabled && !!currentLabel,
    placeholderData: keepPreviousData,
  });

  return {
    contractsOut: data?.results ?? [],
    count: data?.count ?? 0,
    isLoading,
    isError,
    refetch,
  };
}

interface UseContractOutReturn {
  contractOut: ContractOutDetails | undefined;
  isLoading: boolean;
  refetch: () => Promise<QueryObserverResult<ContractOutDetails, Error>>;
}

export function useContractOut(contractOutId: string | undefined): UseContractOutReturn {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["contract-out", contractOutId],
    queryFn: () => contractsOutApi.getContractOut(contractOutId as string),
    enabled: !!contractOutId,
  });

  return { contractOut: data, isLoading, refetch };
}

export function useCreateContractOut(options: { onSuccess?: (c: ContractOut) => void } = {}) {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: CreateContractOut) => contractsOutApi.createContractOut(payload),
    onSuccess: (contractOut) => {
      queryClient.invalidateQueries({ queryKey: ["contracts-out"] });
      options.onSuccess?.(contractOut);
    },
  });

  return { createContractOut: mutate, isPending };
}

export function usePatchContractOut(options: { onSuccess?: () => void } = {}) {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CreateContractOut> }) =>
      contractsOutApi.patchContractOut(id, updates),
    onSuccess: (contractOut) => {
      queryClient.invalidateQueries({ queryKey: ["contracts-out"] });
      queryClient.invalidateQueries({ queryKey: ["contract-out", contractOut.id] });
      options.onSuccess?.();
    },
  });

  return { patchContractOut: mutate, isPending };
}

export function useDeleteContractOut(options: { onSuccess?: () => void } = {}) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => contractsOutApi.deleteContractOut(id).then(() => id),
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["contracts-out"] });
      queryClient.invalidateQueries({ queryKey: ["contract-out", id] });
      options.onSuccess?.();
    },
  });

  return { deleteContractOutAsync: mutateAsync, isPending };
}

interface UseLicenseesReturn {
  licensees: Licensee[];
  count: number;
  isLoading: boolean;
  refetch: () => void;
}

export function useLicensees({
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  search,
  ordering,
}: {
  page?: number;
  pageSize?: number;
  search?: string;
  ordering?: string;
} = {}): UseLicenseesReturn {
  const { currentLabel } = useOrganizationStore();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      "licensees",
      currentLabel?.id,
      { page, pageSize, search: search?.trim() || null, ordering },
    ],
    queryFn: () =>
      contractsOutApi.getLicensees({
        label_id: currentLabel?.id,
        page,
        page_size: pageSize,
        q: search?.trim() || undefined,
        ordering,
      }),
    enabled: !!currentLabel,
    placeholderData: keepPreviousData,
  });

  return {
    licensees: data?.results ?? [],
    count: data?.count ?? 0,
    isLoading,
    refetch,
  };
}

export function useLicensee(licenseeId: string | undefined) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["licensee", licenseeId],
    queryFn: () => contractsOutApi.getLicensee(licenseeId as string),
    enabled: !!licenseeId,
  });

  return { licensee: data, isLoading, refetch };
}

export function useLicenseeLicensedTracks(licenseeId: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: ["licensee-licensed-tracks", licenseeId],
    queryFn: () => contractsOutApi.getLicenseeLicensedTracks(licenseeId as string),
    enabled: !!licenseeId,
  });

  return { licensedTracks: data ?? [], isLoading };
}

export function useUpdateLicensee(options: { onSuccess?: () => void } = {}) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CreateLicensee> }) =>
      contractsOutApi.updateLicensee(id, updates),
    onSuccess: (licensee) => {
      queryClient.invalidateQueries({ queryKey: ["licensees"] });
      queryClient.invalidateQueries({ queryKey: ["licensee", licensee.id] });
      // A licensee's name rides along on its contracts out.
      queryClient.invalidateQueries({ queryKey: ["contracts-out"] });
      options.onSuccess?.();
    },
  });

  return { updateLicenseeAsync: mutateAsync, isPending };
}

export function useDeleteLicensee(options: { onSuccess?: () => void } = {}) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => contractsOutApi.deleteLicensee(id).then(() => id),
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["licensees"] });
      queryClient.invalidateQueries({ queryKey: ["licensee", id] });
      options.onSuccess?.();
    },
  });

  return { deleteLicenseeAsync: mutateAsync, isPending };
}

export function useCreateLicensee(options: { onSuccess?: (licensee: Licensee) => void } = {}) {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: CreateLicensee & { label: string }) =>
      contractsOutApi.createLicensee(payload),
    onSuccess: (licensee) => {
      queryClient.invalidateQueries({ queryKey: ["licensees"] });
      options.onSuccess?.(licensee);
    },
  });

  return { createLicensee: mutate, isPending };
}
